import React, { useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Switch,
  Textarea,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useQueryClient } from "@tanstack/react-query";

import SelectComponent from "../form/SelectComponent";

import { MdOutlineAutoMode } from "react-icons/md";
import { CgUnavailable } from "react-icons/cg";
import addUserImgModal from "../../../adduser-modal.png";

type FormValues = {
  status_automation: string;
  force_hitl: boolean;
  customer_instruction: string;
  selectcustomer: {
    value: string;
    label: string;
  };
};

export default function AddCustomerModal({ isOpen, onClose, classNames }) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    control,
    reset,
    watch,
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      status_automation: "pending_review",
      force_hitl: false,
      customer_instruction: "",
      selectcustomer: { value: "", label: "" },
    },
  });
  const [loading] = useState(false);
  const selectedCustomer = watch("selectcustomer");
  const isCustomerSelected = !!selectedCustomer?.value;

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const formattedData = {
      status_automation: data.status_automation,
      force_hitl: data.force_hitl,
      customer_instruction: data.customer_instruction,
      customer_id: data.selectcustomer.value,
      customer_name: data.selectcustomer.label.split('-').map(str => str.trim())[1] || "", 
    };
    await axiosPrivate.post(`/customers/settings`, formattedData);

    reset();
    onClose();
    queryClient.invalidateQueries({ queryKey: ["CustomerSettings"] });
  };

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop:
          "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <ModalBody>
          <div className="flex pt-5">
            <div>
              <div className="text-2xl font-bold pt-3 pb-6">
                {t("add_customer_settings")}
              </div>
              <div className="text-vistagreen">
                {t("fill_required_info_for_customer_settings")}
              </div>
            </div>
            <img
              src={addUserImgModal}
              alt={t("alt_vistaflow_customer_settings")}
              className="w-48 origin-left self-center"
            />
          </div>

          <div className="space-y-6">
            <SelectComponent
              name="selectcustomer"
              control={control}
              apiUrl="customers"
              endpoint="customers"
              onSelectionChange={(newValue) => {}}
              // isRequired
              defaultValue={null}
              customerId={undefined}
              productId={undefined}
              valueConfidence={undefined}
              isModified={false}
            />

            <Controller
              name="status_automation"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch
                  size="md"
                  color="primary"
                  startContent={<MdOutlineAutoMode />}
                  endContent={<CgUnavailable />}
                  isSelected={value === "auto"}
                  onValueChange={(isSelected) =>
                    onChange(isSelected ? "auto" : "pending_review")
                  }
                  className="mr-4"
                >
                  {t("mode_auto")}
                </Switch>
              )}
            />

            <Controller
              name="force_hitl"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch
                  size="md"
                  color="primary"
                  startContent={<MdOutlineAutoMode />}
                  endContent={<CgUnavailable />}
                  isSelected={value === true}
                  onValueChange={(isSelected) =>
                    onChange(isSelected)
                  }
                >
                  {t("customer_to_verify")}
                </Switch>
              )}
            />

            <Controller
              name="customer_instruction"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label={t("instructions")}
                  placeholder={t("enter_your_instructions")}
                />
              )}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isCustomerSelected}
            className="bg-vistagreen text-white disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("add_customer_settings")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
